import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { 
  Clock, 
  TrendingUp, 
  Users, 
  Lock, 
  Star, 
  Zap,
  Trophy,
  Target,
  DollarSign,
  Eye,
  Heart,
  Share2,
  Bot
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface Tip {
  id: string;
  sport: string;
  league: string;
  match_info: string;
  tip_type: string;
  odds: number;
  confidence: number;
  price: number;
  currency: string;
  is_premium: boolean;
  is_ai_generated: boolean;
  match_date: string;
  expires_at: string;
  status: string;
  view_count: number;
  purchase_count: number;
  created_at: string;
  tipster?: {
    id: string;
    profile: {
      display_name: string;
      avatar_url?: string;
    };
    win_rate: number;
    roi_percentage: number;
    is_premium: boolean;
  };
  ai_persona?: {
    name: string;
    avatar_url?: string;
  };
}

interface PremiumTipCardProps {
  tip: Tip;
  onPurchase?: (tipId: string) => void;
}

export function PremiumTipCard({ tip, onPurchase }: PremiumTipCardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [timeLeft, setTimeLeft] = useState('');
  const [isPurchased, setIsPurchased] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Safe date parsing helper
  const parseDate = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr) return null;
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    const date = parseDate(dateStr);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    const expiryDate = parseDate(tip.expires_at);
    if (!expiryDate) {
      setTimeLeft('No expiry');
      return;
    }

    const updateTimer = () => {
      const now = new Date().getTime();
      const difference = expiryDate.getTime() - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) {
          setTimeLeft(`${days}d ${hours}h`);
        } else {
          setTimeLeft(`${hours}h ${minutes}m`);
        }
      } else {
        setTimeLeft(`Expired`);
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [tip.expires_at]);

  useEffect(() => {
    if (user) {
      checkPurchaseStatus();
    }
  }, [user, tip.id]);

  const checkPurchaseStatus = async () => {
    try {
      const { data } = await supabase
        .from('purchases')
        .select('*')
        .eq('tip_id', tip.id)
        .eq('buyer_id', user?.id)
        .eq('status', 'completed')
        .single();

      setIsPurchased(!!data);
    } catch (error) {
      // No purchase found
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to purchase tips",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('enhanced-payments', {
        body: {
          action: 'create_stripe_session',
          tip_id: tip.id
        }
      });

      if (error) throw error;

      // Open Stripe checkout in new tab
      window.open(data.url, '_blank');
      
      toast({
        title: "Redirecting to payment...",
        description: "Complete your purchase to access this premium tip",
      });

      onPurchase?.(tip.id);
    } catch (error: any) {
      toast({
        title: "Purchase Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleLike = async () => {
    if (!user) return;
    
    setIsLiked(!isLiked);
    // Add API call to like/unlike tip
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/tips/${tip.id}`;
    if (navigator.share) {
      navigator.share({
        title: `${tip.match_info} - ${tip.tip_type}`,
        text: `Check out this betting tip on BetVerse!`,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link Copied!",
        description: "Tip link copied to clipboard",
      });
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-500';
    if (confidence >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won': return 'bg-green-500';
      case 'lost': return 'bg-red-500';
      case 'pending': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const isExpired = new Date(tip.expires_at) <= new Date();

  return (
    <Card className={`group relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
      tip.is_premium ? 'border-2 border-gradient-to-r from-yellow-400 to-orange-500' : ''
    } ${isExpired ? 'opacity-60' : ''}`}>
      {/* Premium Glow Effect */}
      {tip.is_premium && !isExpired && (
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 to-orange-500/10 animate-pulse" />
      )}

      {/* Status Indicator */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {tip.is_ai_generated && (
          <Badge variant="secondary" className="bg-purple-500/10 text-purple-700 border-purple-500/20">
            <Bot className="h-3 w-3 mr-1" />
            AI
          </Badge>
        )}
        {tip.is_premium && (
          <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
            <Star className="h-3 w-3 mr-1" />
            Premium
          </Badge>
        )}
        <div className={`w-3 h-3 rounded-full ${getStatusColor(tip.status)}`} />
      </div>

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-primary/20">
              <AvatarImage 
                src={tip.is_ai_generated ? tip.ai_persona?.avatar_url : tip.tipster.profile.avatar_url} 
              />
              <AvatarFallback>
                {tip.is_ai_generated ? 
                  tip.ai_persona?.name?.charAt(0) : 
                  tip.tipster.profile.display_name?.charAt(0)
                }
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">
                {tip.is_ai_generated ? tip.ai_persona?.name : tip.tipster?.profile?.display_name}
              </p>
              {tip.tipster && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Trophy className="h-3 w-3" />
                  {tip.tipster.win_rate?.toFixed(1)}% Win Rate
                  <TrendingUp className="h-3 w-3 ml-2" />
                  {tip.tipster.roi_percentage?.toFixed(1)}% ROI
                </div>
              )}
            </div>
          </div>
          
          {/* Time Left */}
          {!isExpired && (
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Expires in</div>
              <div className="font-semibold text-orange-500 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {timeLeft}
              </div>
            </div>
          )}
        </div>

        {/* Match Info */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="bg-primary/5">
              {tip.sport} • {tip.league}
            </Badge>
            <div className="text-sm text-muted-foreground">
              {formatDate(tip.match_date)}
            </div>
          </div>
          
          <h3 className="text-lg font-bold">{tip.match_info}</h3>
          <p className="text-primary font-semibold">{tip.tip_type}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{tip.odds}</div>
                <div className="text-xs text-muted-foreground">Odds</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${getConfidenceColor(tip.confidence)}`}>
                  {tip.confidence}%
                </div>
                <div className="text-xs text-muted-foreground">Confidence</div>
              </div>
            </div>
            
            {tip.price > 0 && (
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">
                  {tip.currency} {tip.price}
                </div>
                <div className="text-xs text-muted-foreground">Premium Tip</div>
              </div>
            )}
          </div>
        </div>

        {/* Confidence Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span>Confidence Level</span>
            <span className={getConfidenceColor(tip.confidence)}>{tip.confidence}%</span>
          </div>
          <Progress value={tip.confidence} className="h-2" />
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {tip.view_count} views
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {tip.purchase_count} bought
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleLike} aria-label={isLiked ? "Unlike tip" : "Like tip"} aria-pressed={isLiked}>
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleShare} aria-label="Share tip">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {tip.price > 0 && !isPurchased && !isExpired ? (
            <Button 
              className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              onClick={handlePurchase}
              disabled={processing}
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Buy for {tip.currency} {tip.price}
                </>
              )}
            </Button>
          ) : tip.price === 0 || isPurchased ? (
            <Button variant="outline" className="flex-1" disabled={isExpired}>
              <Target className="h-4 w-4 mr-2" />
              {isPurchased ? 'Purchased' : 'Free Tip'}
            </Button>
          ) : (
            <Button variant="outline" className="flex-1" disabled>
              <Clock className="h-4 w-4 mr-2" />
              Expired
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}